import { FC } from 'react';
import { useTranslations } from 'next-intl';
import Price from 'components/price';
import { PurifiedHtml } from 'components/purified-html';
import { CatHeading, CatTextLink } from 'components/blocks-components';
import { DealerData } from 'types/parts-diagram';
import { DealerResponse } from 'services/dealer';
import { ReplacementParts } from 'services/dealerPriceAvailability/types';
import Image from 'next/image';
import { PRODUCT_FALLBACK_IMAGE } from 'constants/common';
import styles from '../reman-parts/reman-parts.module.scss';
import cx from 'classnames';
import { Conditional } from '@cat-ecom/pcc-components';
import CommonAvailabilityWrapper from '../common-availability-wrapper';
import CheckPriceButton from '../check-price-button/check-price-button';
import AddToCart from 'components/actions/add-to-cart/add-to-cart';
import ContactDealerMobileDrawer from 'components/contact-dealer-mobile-drawer';
interface ProductCardProps {
  remanItem;
  locale: string;
  dealerData: DealerData;
  dealerInformation: DealerResponse;
  availRemoveInterimStockFlag: boolean;
  isShowSourcingDetailEnabled: boolean;
  isDeliveryEnabled: boolean;
  currency: string;
  replacementParts?: ReplacementParts[];
  setDrawerActive?: (drawerActive: boolean) => void;
  handleOpen?: (partNumber: string, price: unknown) => Promise<void>;
  isPccReleaseRemanReplacement?: boolean;
  enableAddToCart?: boolean;
  catcorp?: boolean;
  isRTL?: boolean;
  currencyLdFeatureFlag?: boolean;
}

const ProductReusableCard: FC<ProductCardProps> = ({
  remanItem,
  locale,
  dealerData,
  dealerInformation,
  availRemoveInterimStockFlag,
  isShowSourcingDetailEnabled,
  isDeliveryEnabled,
  currency,
  replacementParts = [],
  setDrawerActive,
  handleOpen,
  isPccReleaseRemanReplacement = false,
  enableAddToCart,
  catcorp,
  isRTL = false,
  currencyLdFeatureFlag = false
}) => {
  const t = useTranslations();

  return (
    <>
      <div className="card-body d-flex align-items-center">
        <Image
          src={remanItem?.imageURL || PRODUCT_FALLBACK_IMAGE}
          loading="lazy"
          width={80}
          data-testid="product-img"
          height={80}
          unoptimized
          className={styles['product-image']}
          alt={remanItem.partNumber}
        />
        <div className={cx(styles['item-details'], 'flex-grow-1')}>
          <Conditional
            test={!!setDrawerActive}
            fallback={
              <CatTextLink
                href={remanItem.partNumber}
                data-testid="product-title"
                className={cx('p-0')}
              >
                <PurifiedHtml
                  htmlString={`${remanItem.partNumber} : ${remanItem.name}`}
                />
              </CatTextLink>
            }
          >
            <CatTextLink
              data-testid="product-title"
              className={cx('p-0')}
              onClick={() => {
                setDrawerActive(true);
                handleOpen(remanItem.partNumber, remanItem);
              }}
            >
              <PurifiedHtml
                htmlString={`${remanItem.partNumber} : ${remanItem.name}`}
              />
            </CatTextLink>
          </Conditional>

          <div className="text-start mt-0.5 mb-0.5">
            <Price
              price={remanItem}
              currency={currency}
              isCoreDepositMobileDrawer
              isMobileView
              isRTL={isRTL}
              currencyLdFeatureFlag={currencyLdFeatureFlag}
            />
            <CommonAvailabilityWrapper
              availabilityData={remanItem}
              shouldDisplayLabel={false}
              isLoading={false}
              locale={locale}
              dealerData={dealerData}
              dealerInformation={dealerInformation}
              availRemoveInterimStockFlag={availRemoveInterimStockFlag}
              isShowSourcingDetailEnabled={isShowSourcingDetailEnabled}
              isDeliveryEnabled={isDeliveryEnabled}
              openInDrawer={true}
              isPccReleaseRemanReplacement={isPccReleaseRemanReplacement}
            />
          </div>
        </div>
      </div>
      <div
        className={cx('mt-4 d-flex align-items-center', {
          'justify-content-between': replacementParts.length,
          'justify-content-end': !replacementParts.length
        })}
      >
        <Conditional test={replacementParts.length > 0}>
          <CatHeading data-testid="recommended-qty" variant="label-sm">
            {t('SSRO_RECOMMENDEDQTY')}
            {remanItem.quantity}
          </CatHeading>
        </Conditional>
        <Conditional test={!catcorp}>
          <Conditional
            test={enableAddToCart}
            fallback={
              <div className="justify-content-end">
                <ContactDealerMobileDrawer
                  locale={locale}
                  dealerAddress={dealerData?.address}
                  fullWidth={false}
                />
              </div>
            }
          >
            <AddToCart productPrice={remanItem} isAddToCartMobile />
          </Conditional>
        </Conditional>
        <Conditional test={catcorp}>
          <div data-testid="check-price-button">
            <CheckPriceButton fullwidth={false} />
          </div>
        </Conditional>
      </div>
    </>
  );
};

export default ProductReusableCard;
